import { Injectable } from '@angular/core';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { take } from 'rxjs/operators';
import { ImageViewerComponent } from '../components/image-viewer/image-viewer.component';

/**
 * Service to open a fullscreen image viewer overlay.
 * Uses Angular CDK Overlay to display ImageViewerComponent.
 */
@Injectable({ providedIn: 'root' })
export class ImageViewerService {

  private overlayRef: OverlayRef | null = null;

  constructor(private overlay: Overlay) {}

  /**
   * Opens the fullscreen image viewer for the given image URL.
   * If an overlay is already open, it is closed first.
   */
  open(imageUrl: string): void {
    this.close();

    this.overlayRef = this.overlay.create({
      hasBackdrop: true,
      backdropClass: 'image-viewer-backdrop',
      positionStrategy: this.overlay.position().global().centerHorizontally().centerVertically(),
      scrollStrategy: this.overlay.scrollStrategies.block(),
      width: '100vw',
      height: '100vh'
    });

    this.overlayRef.backdropClick().pipe(take(1)).subscribe(() => this.close());

    const portal = new ComponentPortal(ImageViewerComponent);
    const componentRef = this.overlayRef.attach(portal);
    componentRef.instance.imageUrl = imageUrl;
    componentRef.instance.closeViewer.pipe(take(1)).subscribe(() => this.close());
  }

  /** Closes the overlay and cleans up resources. */
  close(): void {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
  }
}
